import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import PageTitle from '../../components/PageTitle';
import { Alert } from 'react-bootstrap';
import styles from './PatientMessages.module.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:8000/api/';

const PatientMessages = () => {
    const { id } = useParams();
    const navigate = useNavigate();
    const messagesEndRef = useRef(null);
    const pollingIntervalRef = useRef(null);
    const messagesContainerRef = useRef(null);
    const shouldAutoScrollRef = useRef(true);
    
    const [patient, setPatient] = useState(null);
    const [messages, setMessages] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [messageBody, setMessageBody] = useState('');
    const [sending, setSending] = useState(false);
    const [sendError, setSendError] = useState('');

    const scrollToBottom = () => {
        messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    // Check if user is at the bottom of the messages
    const isUserAtBottom = () => {
        if (!messagesContainerRef.current) return true;
        const { scrollTop, scrollHeight, clientHeight } = messagesContainerRef.current;
        // Consider "at bottom" if within 100px of the bottom
        return scrollHeight - scrollTop - clientHeight < 100;
    };

    // Handle scroll event to track if user scrolled up
    const handleScroll = () => {
        shouldAutoScrollRef.current = isUserAtBottom();
    };

    useEffect(() => {
        // Only auto-scroll if user is at the bottom
        if (shouldAutoScrollRef.current) {
            scrollToBottom();
        }
    }, [messages]);

    useEffect(() => {
        fetchPatientAndMessages();
        
        // Set up polling to fetch new messages every 5 seconds
        pollingIntervalRef.current = setInterval(() => {
            fetchMessagesOnly();
        }, 5000);
        
        // Cleanup on unmount
        return () => {
            if (pollingIntervalRef.current) {
                clearInterval(pollingIntervalRef.current);
            }
        };
    }, [id]);

    const fetchPatientAndMessages = async () => {
        try {
            setLoading(true);
            setError('');

            const [patientRes, messagesRes] = await Promise.all([
                axios.get(`${API_URL}patients/${id}`, {
                    headers: { 'Accept': 'application/json' }
                }),
                axios.get(`${API_URL}messages/conversation/${id}`, {
                    headers: { 'Accept': 'application/json' }
                })
            ]);

            setPatient(patientRes.data);
            setMessages(messagesRes.data.conversation || []);
        } catch (err) {
            console.error('Error fetching data:', err);
            if (err.response?.status === 401) {
                navigate('/control/login');
            } else {
                setError(err.response?.data?.error || 'Failed to load messages');
            }
        } finally {
            setLoading(false);
        }
    };

    const fetchMessagesOnly = async () => {
        try {
            const messagesRes = await axios.get(`${API_URL}messages/conversation/${id}`, {
                headers: { 'Accept': 'application/json' }
            });

            setMessages(messagesRes.data.conversation || []);
        } catch (err) {
            console.error('Error fetching messages:', err);
            if (err.response?.status === 401) {
                navigate('/control/login');
            }
        }
    };

    const handleSendMessage = async (e) => {
        e.preventDefault();
        
        if (!messageBody.trim()) return;

        try {
            setSending(true);
            setSendError('');

            // Set flag to auto-scroll when sending a message
            shouldAutoScrollRef.current = true;
            
            await axios.post(
                `${API_URL}messages/send`,
                {
                    patient_id: parseInt(id),
                    sender: 'doctor',
                    message_body: messageBody
                },
                {
                    headers: { 'Accept': 'application/json' }
                }
            );

            setMessageBody('');
            await fetchMessagesOnly();
        } catch (err) {
            console.error('Error sending message:', err);
            setSendError(err.response?.data?.error || 'Failed to send message');
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return (
            <div className="d-flex justify-content-center align-items-center vh-100">
                <div className="spinner-border text-primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="patient-list-container">
                <div className="container py-4">
                    <Alert variant="danger">{error}</Alert>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.pageContainer}>
            <PageTitle title={`Messages - ${patient?.name || 'Patient'}`} />
            
            <div className={styles.messagesCard}>
                    <div className={styles.messagesContainer}>
                        <div className={styles.messagesList} ref={messagesContainerRef} onScroll={handleScroll}>
                                {messages.length === 0 ? (
                                    <div className={styles.noMessages}>
                                        <i className="fas fa-inbox fa-3x mb-3 text-muted"></i>
                                        <p className="text-muted">No messages yet. Start the conversation!</p>
                                    </div>
                                ) : (
                                    messages.map((msg) => (
                                        <div 
                                            key={msg.id} 
                                            className={`${styles.messageBubble} ${msg.sender === 'doctor' ? styles.messageDoctor : styles.messagePatient}`}
                                        >
                                            <div className={styles.messageHeader}>
                                                <span className={styles.messageSender}>
                                                    {msg.sender === 'doctor' ? (
                                                        <>
                                                            <i className="fas fa-user-md me-1"></i>
                                                            Doctor
                                                        </>
                                                    ) : (
                                                        <>
                                                            <i className="fas fa-user me-1"></i>
                                                            Patient
                                                        </>
                                                    )}
                                                </span>
                                                <span className={styles.messageTime}>
                                                    {new Date(msg.created_at).toLocaleString('en-US', {
                                                        year: 'numeric',
                                                        month: 'short',
                                                        day: 'numeric',
                                                        hour: '2-digit',
                                                        minute: '2-digit'
                                                    })}
                                                </span>
                                            </div>
                                            <div className={styles.messageBody}>
                                                {msg.message_body}
                                            </div>
                                        </div>
                                    ))
                                )}
                                <div ref={messagesEndRef} />
                            </div>

                            <div className={styles.messageInputContainer}>
                                {sendError && (
                                    <Alert variant="danger" dismissible onClose={() => setSendError('')}>
                                        {sendError}
                                    </Alert>
                                )}
                                <form onSubmit={handleSendMessage} className={styles.messageInputForm}>
                                    <div className={styles.inputGroup}>
                                        <textarea
                                            className={`form-control ${styles.messageTextarea}`}
                                            placeholder="Type your message..."
                                            value={messageBody}
                                            onChange={(e) => setMessageBody(e.target.value)}
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter' && !e.shiftKey) {
                                                    e.preventDefault();
                                                    handleSendMessage(e);
                                                }
                                            }}
                                            disabled={sending}
                                            rows="3"
                                        />
                                        <button 
                                            type="submit" 
                                            className={`btn btn-primary ${styles.sendButton}`}
                                            disabled={!messageBody.trim() || sending}
                                        >
                                            {sending ? (
                                                <>
                                                    <span className="spinner-border spinner-border-sm" role="status"></span>
                                                    <span className={styles.sendButtonText}>Sending...</span>
                                                </>
                                            ) : (
                                                <>
                                                    <i className="fas fa-paper-plane"></i>
                                                    <span className={styles.sendButtonText}>Send</span>
                                                </>
                                            )}
                                        </button>
                                    </div>
                                    <small className="text-muted">Press Enter to send, Shift+Enter for new line</small>
                                </form>
                            </div>
                        </div>
                    </div>
        </div>
    );
};

export default PatientMessages;
